import React, { useEffect, useState, useRef } from 'react';
import { Form, Input, Upload, Space, Button, Popover, Radio, Tabs, Badge, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import FileUtils from '@/script/FileUtils';
import Modal from '@/components/Modal/Modal';
import usePermission from '@/hooks/usePermission.mjs';
import { getBaseline } from "@/script/SelectDb.mjs";

const Pane = ({ apiMarkMethod, prjId, sVer, prjNo, prjName, fileIds, pkids, dpId, type, unattended, onCall, onOk, onCancel, ...props }) => {
    const http = useHTTP();
    const { TabPane } = Tabs;
    const [form] = Form.useForm();

    const ref = useRef({});
    const [bds, setBds] = useState([]);
    const [uis, setUis] = useState([]);
    const [sss, setSss] = useState([]);
    const [fileList, setFileList] = useState([]);

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [bdLoading, setBdLoading] = useState(false);
    const [uiLoading, setUiLoading] = useState(false);
    const [ssLoading, setSsLoading] = useState(false);
    const [apiMarkMethods, setApiMarkMethods] = useState([]);

    // 多文件上传
    const isMultiple = ['importBdRuleStepForFiles', 'importBdRuleStepForFilesV2', 'importDoDe', 'importBdLogicAndUcStep', 'importBdLogicUcStep', 'importUiModule', "importUiAlgV2", "importUiAlg2V2", "importBdSummary"].includes(type);
    // 上传文件类型（JSON导入直接上传.json）
    const accept = 'importBdRuleStepForJsonV2' == type ? '.json' : '.xls,.xlsx';
    // 不需要上传文件
    const noFile = ['generateDo', 'importBdRuleForAi', 'importBdRuleStepForAi', 'importBdRuleStepForAiFiles', 'importBdRuleStepForAiFilesV2', 'importUiAlgForAi', 'importUiAlgForAiV2', 'bdRuleForStruts', 'importUiAlg2ForAi', 'importUiAlg2ForAiV2', 'importDoDeForAi', 'importBdLogicAndUcStepForAi', 'importBdLogicUcStepForAi', 'importUiModuleForAi', 'importBdRuleDeForAi', 'importDeSample', "importBdSummaryForAi", 'importBfForAi', 'importBfcForAi'].includes(type);
    const hasBd = ['generateDo', 'importBdRule', 'importBdRuleStep', 'importBdRuleStepForFiles', 'importBdRuleForAi', 'importBdRuleStepForAi', 'importBdRuleStepForAiFiles'].includes(type);
    const hasSs = ['importBd', 'importUi', 'importUiAlg', 'importUiAlgForAi', 'importUi2', 'importSsStructure'].includes(type);
    const hasUi = ['importUiAlg2', 'importUiAlg2ForAi'].includes(type);
    const hasApiMarkMethod = ['importUiAlgV2'].includes(type);

    // 导入权限
    const crtBf = usePermission(['BF_CRT'], [prjId + sVer], []);
    const uptBf = usePermission(['BF_UPT'], [prjId + sVer], []);
    const crtBd = usePermission(['BD_CRT'], [prjId + sVer], []);
    const uptBd = usePermission(['BD_UPT'], [prjId + sVer], []);
    const crtSf = usePermission(['SF_CRT'], [prjId + sVer], []);
    const crtUi = usePermission(['UI_CRT'], [prjId + sVer], []);
    const uptUi = usePermission(['UI_UPT'], [prjId + sVer], []);
    const crtLs = usePermission(['ARCH_LS_CRT'], [prjId + sVer], []);
    const crtLsi = usePermission(['LSI_CRT'], [prjId + sVer], []);
    const crtSs = usePermission(['ARCH_SS_CRT'], [prjId + sVer], []);

    useEffect(() => {
        (async () => {
            await init();

            if (unattended) {
                importDp()
            }
        })()
    }, []);

    const init = async () => {
        ref.current = {};

        // 查询：基线信息
        const baseline = await getBaseline(prjId, sVer);
        const name = baseline.prjName + '[' + sVer + '-' + baseline.pblName + ']';
        ref.current.subTypeCode = baseline.subTypeCode;

        initBf(); // 业务流程
        initBd(); // 业务能力模块
        initSf(); // 系统功能
        initUi(); // 用户界面
        initUi2(); // 用户界面2
        initLsi();
        initSs(); // 子系统
        doQueryApiMarkMethods(); // API标记方式

        setFileList([]);
        form.resetFields();
        form.setFieldsValue({ prjName: name });
    }

    const doQueryApiMarkMethods = async () => {
        if (!hasApiMarkMethod) {
            return;
        }
        const data = await runAsync(
            async () => {
                return await http.get(`/basdatadictdtls`, { dictCode: 'API_MARK_METHOD' });
            }, {
            error: { content: (err) => `查询api标记方式失败: ${err.message}` },
            loading: { show: false },
            success: { show: false },
        });
        if (data) {
            setApiMarkMethods(data.map(({ code, nameCn }) => ({ value: code, label: nameCn })))
        }
    }

    const initBf = () => {
        if ('importBf' == type) {
            ref.current.perm = crtBf;
            ref.current.url = '/bmbfs/';
            ref.current.title = '导入业务流程'; // 默认：流程域
        } else if ('importBfForAi' == type) {
            ref.current.perm = crtBf;
            ref.current.url = '/bmbfs/';
            ref.current.title = '导入业务流程'; // 默认：流程域
        } else if ('importBfForPm' == type) {
            ref.current.perm = crtBf;
            ref.current.url = '/bmbfs/';
            ref.current.title = '导入业务流程(BODA2)'; // BODA2：流程域
        } else if ('importBfForBm' == type) {
            ref.current.perm = crtBf;
            ref.current.url = '/bmbfs/';
            ref.current.title = '导入业务流程(BODA1)'; // BODA1：业务域
        } else if ('importBfcForAi' == type) {
            ref.current.perm = uptBf;
            ref.current.url = '/bmbfs/';
            ref.current.title = 'AI构建业务流程集成用例';
        }
    }

    const initBd = () => {
        const isBoda2 = 'BODA2' == ref.current.subTypeCode;

        if ('importBd' == type) {
            ref.current.perm = crtBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入业务能力模块清单';

            if (isBoda2) {
                ref.current.title = '导入程序模块清单';
            }

            selectSs();
        } else if (['importBdRule', 'importBdRuleForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入业务事务能力清单';

            if (isBoda2) {
                ref.current.title = '导入程序能力清单';
            }

            selectBd();
        } else if (['importBdRuleStep', 'importBdRuleStepForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入业务事务能力步骤清单';

            if (isBoda2) {
                ref.current.title = '导入程序能力步骤清单';
            }

            selectBd();
        } else if (['importBdRuleStepForFiles', 'importBdRuleStepForAiFiles'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入业务事务能力';

            if (isBoda2) {
                ref.current.title = '导入程序能力';
            }

            selectBd();
        } else if (['importBdRuleStepForFilesV2', 'importBdRuleStepForAiFilesV2'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '是否确定导入模型';
        } else if ('importBdRuleStepForJsonV2' == type) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '是否确定导入模型';
        } else if ('generateDo' == type) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '生成数据对象';
            selectBd();
        } else if (['importDoDe', 'importDoDeForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '是否确定导入模型';
            selectSs();
        }
        // 正向建模：程序能力
        else if (['importBdLogicAndUcStep', 'importBdLogicAndUcStepForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/forward/';
            ref.current.title = '是否确定导入模型';
        }
        else if (['importBdLogicUcStep', 'importBdLogicUcStepForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/forward/';
            ref.current.title = '是否确定导入模型';
        }
        else if (['importUiModule', 'importUiModuleForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/forward/';
            ref.current.title = '是否确定导入模型';
        }
        else if (['importBdSummary', 'importBdSummaryForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/forward/';
            ref.current.title = '是否确定导入模型';
        }
        // 数据表
        else if (['importBdRuleDe', 'importBdRuleDeForAi'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入数据表';
        }
        else if (['importDeSample'].includes(type)) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = '导入示例数据';
        }
    }

    const initSf = () => {
        if ('importSfForUi' == type) {
            ref.current.perm = crtSf;
            ref.current.url = '/sf/';
            ref.current.title = '导入交互功能用例';
        }
    }

    const initUi = () => {
        if ('importUi' == type) {
            ref.current.perm = crtUi;
            ref.current.url = '/umuis/';
            ref.current.title = '导入用户界面';
            selectSs();
        } else if (['importUiAlg', 'importUiAlgForAi'].includes(type)) {
            ref.current.perm = uptUi;
            ref.current.url = '/umuis/';
            ref.current.title = '导入用户界面'; // 界面能力步骤清单
            selectSs();
        } else if (['importUiAlgV2', 'importUiAlgForAiV2'].includes(type)) {
            ref.current.perm = uptUi;
            ref.current.url = '/umuis/';
            ref.current.title = '是否确定导入模型'; // 界面能力步骤清单
        } else if ('bdRuleForStruts' == type) {
            ref.current.perm = uptBd;
            ref.current.url = '/reverse/';
            ref.current.title = 'STRUTS程序能力处理';
            selectBd();
        } else if ('importOpenUi' == type) {
            ref.current.perm = uptUi;
            ref.current.url = '/umuis/';
            ref.current.title = 'VUE用户界面处理';
            selectUi();
        }
    }

    const initUi2 = () => {
        if ('importUi2' == type) {
            ref.current.perm = crtUi;
            ref.current.url = '/umuis/';
            ref.current.title = '导入用户界面清单';
            selectSs();
        }
        else if (['importUiAlg2', 'importUiAlg2ForAi'].includes(type)) {
            ref.current.perm = uptUi;
            ref.current.url = '/umuis/';
            ref.current.title = '导入界面事件能力';
            selectUi();
        }
        else if (['importUiAlg2V2', 'importUiAlg2ForAiV2'].includes(type)) {
            ref.current.perm = uptUi;
            ref.current.url = '/umuis/';
            ref.current.title = '是否确定导入模型';
        }
    }

    const initLsi = () => {
        if ('importLs' == type) {
            ref.current.perm = crtLs;
            ref.current.url = '/archlss/';
            ref.current.title = '导入外联系统';
        } else if ('importLsi' == type) {
            ref.current.perm = crtLsi;
            ref.current.url = '/imlsis/';
            ref.current.title = '导入外联接口';
        }
    }

    const initSs = () => {
        if ('importSs' == type) {
            ref.current.perm = crtSs;
            ref.current.url = '/archsss/';
            ref.current.title = '导入子系统';
        } else if ('importSsStructure' == type) {
            ref.current.perm = crtSs;
            ref.current.url = '/archsss/';
            ref.current.title = '导入程序结构';
            selectSs();
        }
    }

    // 查询：业务能力模块/服务
    const selectBd = async () => {
        const isPk = 'bdRuleForStruts' == type;

        try {
            if (prjId && sVer) {
                setBdLoading(true);
                const data = await runAsync(
                    async () => {
                        return await http.get('/dmbds', { prjId, sVer });
                    }, {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: false },
                    success: { show: false },
                });

                const options = [];

                for (const bd of data) {
                    options.push({ value: isPk ? bd.pkid : bd.bdCode, label: bd.bdNo + '/' + bd.bdName });
                }

                setBds(options);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setBdLoading(false);
        }
    }

    // 查询：用户界面
    const selectUi = async () => {
        const isPk = 'importOpenUi' == type;

        try {
            if (prjId && sVer) {
                setUiLoading(true);
                const data = await runAsync(
                    async () => {
                        return await http.get('/umuis', { prjId, sVer });
                    }, {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: false },
                    success: { show: false },
                });

                const options = [];

                for (const ui of data) {
                    options.push({ value: isPk ? ui.pkid : ui.uiCode, label: ui.uiUserCode + '/' + ui.uiName });
                }

                setUis(options);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setUiLoading(false);
        }
    }

    // 查询：子系统
    const selectSs = async () => {
        try {
            if (prjId && sVer) {
                setSsLoading(true);
                const data = await runAsync(
                    async () => {
                        return await http.get('/archsss', { prjId, sVer });
                    }, {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: false },
                    success: { show: false },
                });

                const options = [];

                for (const ss of data) {
                    options.push({ value: ss.ssCode, label: ss.ssUserCode + '/' + ss.ssName });
                }

                setSss(options);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setSsLoading(false);
        }
    }

    // 导入：模型制品
    const importDp = async () => {
        try {
            setLoading(true);
            const formField = await form.validateFields();

            if (fileList.length == 0 && !noFile) {
                return Modal.info({ content: '请选择上传文件' });
            }

            const url = ref.current.url + type;
            const param = { apiMarkMethod, prjId, sVer, files: fileList };
            const header = { headers: { 'Content-Type': 'multipart/form-data' } };

            // 赋值
            // param.bdCode = formField.bdCode ?? null; // 业务能力模块
            // param.uiCode = formField.uiCode ?? null; // 用户界面
            // param.ssCode = formField.ssCode ?? null; // 子系统
            // param.pkids = formField.pkids ?? null; // 模件ID
            Object.assign(param, formField);

            // AI生成文件id
            if (fileIds) {
                param.fileIds = fileIds;
            }

            // 库表域id
            if (pkids) {
                param.pkids = pkids;
            }

            // 程序模块id
            if (dpId) {
                param.dpId = dpId;
            }

            const data = await runAsync(
                async () => {
                    return await http.post(url, param, header);
                }, {
                error: { content: (err) => `导入失败: ${err.message}` },
                loading: { show: true },
                success: { show: true, content: () => '导入成功' },
            });

            onOk(true);
            doInfo(data); // 打印导入日志

            if (onCall) {
                onCall();
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const BadgeExt = ({ name, count }) => {
        return <Badge size="small" offset={[10, 0]} count={count}>{name}</Badge>;
    }

    const doInfo = (data) => {
        // 新增/修改
        const adds = data?.adds;
        const upts = data?.upts;

        // 业务流程
        const bfs = data?.bfs;

        // 业务能力模块/服务
        const bds = data?.bds;
        const rules = data?.rules;
        const algs = data?.algs;
        const commonRules = data?.commonRules;

        // 系统功能
        const sfs = data?.sfs;

        // 用户界面
        const uis = data?.uis;
        const uiRules = data?.uiRules;
        const ues = data?.ues;

        // 数据表
        const dts = data?.dts;

        // 子系统
        const sss = data?.sss;

        // 通用
        const infos = data?.infos;
        const invalids = data?.invalids;
        const repeats = data?.repeats;
        const exists = data?.exists;
        const notExists = data?.notExists;

        return Modal.info({
            width: 800,
            title: '处理结果',
            content: <>
                <Tabs type="list" style={{ overflow: 'hidden' }}>
                    {
                        adds && adds.length > 0 &&
                        <TabPane key="adds" tab={<BadgeExt name="新增的记录" count={adds.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {adds.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        upts && upts.length > 0 &&
                        <TabPane key="upts" tab={<BadgeExt name="修改的记录" count={upts.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {upts.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        bfs && bfs.length > 0 &&
                        <TabPane key="bfs" tab={<BadgeExt name="业务流程" count={bfs.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {bfs.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        bds && bds.length > 0 &&
                        <TabPane key="bds" tab={<BadgeExt name={getName()} count={bds.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {bds.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        commonRules && commonRules.length > 0 &&
                        <TabPane key="commonRules" tab={<BadgeExt name={"业务规则"} count={commonRules.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {commonRules.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        algs && algs.length > 0 &&
                        <TabPane key="algs" tab={<BadgeExt name={"用户场景"} count={algs.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {algs.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        rules && rules.length > 0 &&
                        <TabPane key="rules" tab={<BadgeExt name={getName2()} count={rules.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {rules.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        sfs && sfs.length > 0 &&
                        <TabPane key="sfs" tab={<BadgeExt name="系统功能" count={sfs.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {sfs.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        uis && uis.length > 0 &&
                        <TabPane key="uis" tab={<BadgeExt name="用户界面" count={uis.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {uis.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        uiRules && uiRules.length > 0 &&
                        <TabPane key="uiRules" tab={<BadgeExt name={"数据控件"} count={uiRules.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {uiRules.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        ues && ues.length > 0 &&
                        <TabPane key="ues" tab={<BadgeExt name={"界面事件能力"} count={ues.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {ues.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        dts && dts.length > 0 &&
                        <TabPane key="dts" tab={<BadgeExt name="数据表" count={dts.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {dts.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        sss && sss.length > 0 &&
                        <TabPane key="sss" tab={<BadgeExt name="子系统" count={sss.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {sss.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        infos && infos.length > 0 &&
                        <TabPane key="infos" tab={<BadgeExt name="信息" count={infos.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {infos.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        invalids && invalids.length > 0 &&
                        <TabPane key="invalids" tab={<BadgeExt name="不合法的记录" count={invalids.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {invalids.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        repeats && repeats.length > 0 &&
                        <TabPane key="repeats" tab={<BadgeExt name="重复的记录" count={repeats.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {repeats.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        exists && exists.length > 0 &&
                        <TabPane key="exists" tab={<BadgeExt name="已存在的记录" count={exists.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {exists.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }

                    {
                        notExists && notExists.length > 0 &&
                        <TabPane key="notExists" tab={<BadgeExt name="不存在的记录" count={notExists.length} />}>
                            <div style={{ listStyle: 'none', height: '300px', overflow: 'auto' }}>
                                {notExists.map(v => <div>{v}</div>)}
                            </div>
                        </TabPane>
                    }
                </Tabs>
            </>
        });
    }

    // 权限控制
    const perm = () => {
        return ref.current.perm ?? false;
    }

    const download = async (type) => {
        let _type = type;

        if ('BODA2' == ref.current.subTypeCode && ['importBd', 'importBdRule', 'importBdRuleStep'].includes(type)) {
            _type = type + 'Boda2';
        }

        try {
            const data = await runAsync(
                async () => {
                    return await http.get(`/api/download?type=` + _type);
                }, {
                error: { content: (err) => `下载失败: ${err.message}` },
                loading: { show: false },
                success: { show: false },
            });

            FileUtils.saveFile(data.blob, data.fileName);
        } catch (err) {
            console.log(err);
        }
    }

    const useStep = <ol>
        <li>能力清单：业务事务能力清单；</li>
        <li>步骤清单：业务事务能力步骤清单；</li>
        <li>可分别导入能力清单、步骤清单；</li>
        <li>可一次导入能力清单与步骤清单；</li>
    </ol>;

    const useStepBoad2 = <ol>
        <li>程序能力清单：程序能力清单；</li>
        <li>步骤清单：程序能力步骤清单；</li>
        <li>可分别导入程序能力清单、步骤清单；</li>
        <li>可一次导入程序能力清单与步骤清单；</li>
    </ol>;

    const useDoDe = <ol>
        <li>AI数据对象元素清单；</li>
        <li>AI数据实体清单；</li>
        <li>可分别导入AI数据对象元素清单、AI数据实体清单；</li>
        <li>可一次导入AI数据对象元素清单与AI数据实体清单；</li>
    </ol>;

    const useLogic = <ol>
        <li>AI业务规则清单；</li>
        <li>AI用户场景清单；</li>
        <li>AI程序能力清单；</li>
        <li>AI功能用例步骤清单；</li>
        <li>可分别导入AI业务规则清单、AI用户场景清单、AI程序能力清单、AI功能用例步骤清单；</li>
        <li>可一次导入AI业务规则清单、AI用户场景清单、AI程序能力清单、AI功能用例步骤清单；</li>
    </ol>;

    const useUi = <ol>
        <li>AI用户界面清单；</li>
        <li>AI数据控件清单；</li>
        <li>AI界面事件能力处理步骤清单；</li>
        <li>可分别导入AI用户界面清单、AI数据控件清单、AI界面事件能力处理步骤清单；</li>
        <li>可一次导入AI用户界面清单、AI数据控件清单、AI界面事件能力处理步骤清单；</li>
    </ol>;

    const getName = () => {
        return 'BODA2' == ref.current.subTypeCode ? '程序模块' : '业务能力模块';
    }

    const getName2 = () => {
        return 'BODA2' == ref.current.subTypeCode ? '程序能力' : '业务事务能力';
    }

    // 下载：能力清单
    const downloadBdList = () => {
        return (
            <>
                <a onClick={() => download('importBdRule')}>{'BODA2' == ref.current.subTypeCode ? '程序能力清单' : '能力清单'}</a>
                <a onClick={() => download('importBdRuleStep')}>步骤清单</a>

                <Popover
                    content={'BODA2' == ref.current.subTypeCode ? useStepBoad2 : useStep}
                    title="导入步骤"
                    trigger="click"
                    open={open}
                    onOpenChange={setOpen}
                >
                    <a>使用说明</a>
                </Popover>
            </>
        );
    }

    // 下载：数据对象/数据实体清单
    const downloadDoDeList = () => {
        return (
            <>
                <a onClick={() => download('importDo')}>AI数据对象元素清单</a>
                <a onClick={() => download('importDe')}>AI数据实体清单</a>
                <a onClick={() => download('importSummary')}>AI用户场景清单</a>
                {/* <a onClick={() => download('importTerm')}>AI业务术语清单</a> */}

                <Popover
                    content={useDoDe}
                    title="导入步骤"
                    trigger="click"
                    open={open}
                    onOpenChange={setOpen}
                >
                    <a>使用说明</a>
                </Popover>
            </>
        );
    }

    // 下载：程序能力
    const downloadLogicList = (isReverse = false) => {
        return !isReverse ? (
                <>
                    <a onClick={() => download('importBdRuleCommon')}>AI业务规则清单</a>
                    <a onClick={() => download('importBdAlg')}>AI用户场景清单</a>
                    <a onClick={() => download('importBdLogic')}>AI程序能力清单</a>
                    <a onClick={() => download('importBdLogicStep')}>AI功能用例步骤清单</a>

                    <Popover
                        content={useLogic}
                        title="导入步骤"
                        trigger="click"
                        open={open}
                        onOpenChange={setOpen}
                    >
                        <a>使用说明</a>
                    </Popover>
                </>
            ) :
            (
                <>
                    <a onClick={() => download('importBdRuleCommon')}>AI业务规则清单</a>
                    <a onClick={() => download('importBdAlgReverse')}>AI用户场景清单</a>
                    <a onClick={() => download('importBdLogicReverse')}>AI程序能力清单</a>
                    <a onClick={() => download('importBdLogicStepReverse')}>AI功能用例步骤清单</a>

                    <Popover
                        content={useLogic}
                        title="导入步骤"
                        trigger="click"
                        open={open}
                        onOpenChange={setOpen}
                    >
                        <a>使用说明</a>
                    </Popover>
                </>
            )
            ;
    }

    // 下载：功能用例
    const downloadLogicUcList = () => {
        return (
            <>
                <a onClick={() => download('importBdLogicStep')}>AI功能用例步骤清单</a>
            </>
        );
    }

    // 正向下载：用户界面
    const downloadUiList = () => {
        return (
            <>
                <a onClick={() => download('importAiUi')}>AI用户界面清单</a>
                <a onClick={() => download('importAiUiRule')}>AI数据控件清单</a>
                <a onClick={() => download('importAiUeStep')}>AI界面事件能力处理步骤清单</a>

                <Popover
                    content={useUi}
                    title="导入步骤"
                    trigger="click"
                    open={open}
                    onOpenChange={setOpen}
                >
                    <a>使用说明</a>
                </Popover>
            </>
        );
    }

    // 逆向下载：用户界面
    const downloadUiReverseList = () => {
        return (
            <>
                <a onClick={() => download('importAiUiReverse')}>AI用户界面清单</a>
                <a onClick={() => download('importAiUiRuleReverse')}>AI数据控件清单</a>
                <a onClick={() => download('importAiUeStepReverse')}>AI界面事件能力处理步骤清单</a>

                <Popover
                    content={useUi}
                    title="导入步骤"
                    trigger="click"
                    open={open}
                    onOpenChange={setOpen}
                >
                    <a>使用说明</a>
                </Popover>
            </>
        );
    }

    // 下载：列表
    const downloadList = () => {
        if ('importBdRuleStepForFiles' == type) {
            return downloadBdList();
        } else if ('importDoDe' == type) {
            return downloadDoDeList();
        } else if (['importBdLogicAndUcStep', 'importBdRuleStepForFilesV2', 'importBdRuleStepForJsonV2'].includes(type)) {
            return downloadLogicList(['importBdRuleStepForFilesV2', 'importBdRuleStepForJsonV2'].includes(type));
        } else if (['importBdLogicUcStep'].includes(type)) {
            return downloadLogicUcList();
        } else if (['importUiModule'].includes(type)) {
            return downloadUiList();
        } else if (['importUiAlgV2', 'importUiAlg2V2'].includes(type)) {
            return downloadUiReverseList();
        } else if (['importBdSummary'].includes(type)) {
            return <></>
        }
        else {
            return <a onClick={() => download(type)}>下载模板</a>;
        }
    }

    return (
        <Modal
            title={ref.current.title}
            okText="确定"
            cancelText="取消"
            width="600px"
            footer={[
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={importDp} loading={loading} disabled={!perm()}>确定</Button>
                </Space>
            ]}
            {...props}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="prjName" label="项目名称" rules={[{ required: true }]}>
                    <Input disabled />
                </Form.Item>

                {
                    hasBd &&
                    <Form.Item
                        name="bdCode"
                        label={getName()}
                        rules={[{ required: true, message: '请选择' + getName() }]}
                    >
                        <Select
                            showSearch
                            loading={bdLoading}
                            options={bds}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input?.toLowerCase())}
                        />
                    </Form.Item>
                }

                {
                    hasUi &&
                    <Form.Item name="uiCode" label="用户界面" rules={[{ required: true, message: '请选择用户界面' }]}>
                        <Select
                            showSearch
                            loading={uiLoading}
                            options={uis}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input?.toLowerCase())}
                        />
                    </Form.Item>
                }

                {
                    hasSs &&
                    <Form.Item name="ssCode" label="子系统" rules={'importSsStructure' == type ? [{ required: true, message: '请选择子系统' }] : undefined}>
                        <Select
                            showSearch
                            loading={ssLoading}
                            options={sss}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input?.toLowerCase())}
                        />
                    </Form.Item>
                }

                {
                    hasApiMarkMethod &&
                    <Form.Item
                        name="apiMarkMethod"
                        label="API标记方式"
                    >
                        <Radio.Group
                            options={apiMarkMethods}
                        />
                    </Form.Item>
                }

                {/* struts程序能力处理 */}
                {
                    'bdRuleForStruts' == type &&
                    <Form.Item
                        name="pkids"
                        label={getName()}
                        rules={[{ required: true, message: '请选择' + getName() }]}
                    >
                        <Select
                            mode={'multiple'}
                            showSearch
                            loading={bdLoading}
                            options={bds}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input?.toLowerCase())}
                        />
                    </Form.Item>
                }

                {/* 用户界面迁移 */}
                {
                    'importOpenUi' == type &&
                    <Form.Item name="pkids" label="用户界面" rules={[{ required: true, message: '请选择用户界面' }]}>
                        <Select
                            mode={'multiple'}
                            showSearch
                            loading={uiLoading}
                            options={uis}
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input?.toLowerCase())}
                        />
                    </Form.Item>
                }

                {
                    !noFile &&
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Upload
                            accept={accept}
                            fileList={fileList}
                            multiple={isMultiple}
                            showUploadList={true}
                            beforeUpload={(file, files) => {
                                if (isMultiple) {
                                    setFileList([...fileList, ...files]);
                                } else {
                                    setFileList(files);
                                }

                                return false;
                            }}
                            onRemove={(file) => {
                                // setFileList([]);
                                if (file) {
                                    const notDeletes = fileList.filter(f => f.name != file.name);
                                    setFileList([...notDeletes]);
                                }
                            }}
                        >
                            <div className="ant-upload-btn bg-grey-light">
                                <PlusOutlined />
                                <div style={{ marginTop: 4 }}>上传</div>
                            </div>
                        </Upload>

                        <Space>
                            {downloadList()}
                        </Space>
                    </div>
                }
            </Form>
        </Modal>
    );
}

export default Pane;
